import { lighten, ThemeOptions } from '@mui/material'
import { grey, red } from '@mui/material/colors'

import { chartComponents } from '@/Theme/charts'
import { primary, secondary } from '@/Theme/colors'
import { tableComponentOverrides } from '@/Theme/table'

const displayCol = {
  display: 'flex',
  flexDirection: 'column'
}

const buttonBoxShadow =
  '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)'

export const components: ThemeOptions['components'] = {
  ...tableComponentOverrides,
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        paddingLeft: '24px',
        paddingRight: '24px'
      }
    }
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        paddingLeft: '24px',
        paddingRight: '24px'
      }
    }
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        margin: 0,
        marginBottom: '4px'
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 16,
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '8px',
        paddingBottom: '8px',
        lineHeight: 1,
        minHeight: '42px'
      },
      sizeSmall: {
        fontWeight: 600,
        fontSize: 14,
        paddingLeft: '8px',
        paddingRight: '8px',
        paddingTop: '4px',
        paddingBottom: '4px',
        minHeight: 'auto'
      },
      outlinedSecondary: {
        color: primary.dark
      }
    },
    variants: [
      {
        props: { variant: 'primaryAction', disableElevation: false },
        style: {
          backgroundColor: '#FFECB3',
          color: '#000',
          boxShadow: buttonBoxShadow,
          '&:hover': {
            backgroundColor: '#ffe082'
          },
          '&.Mui-disabled': {
            backgroundColor: grey[300],
            color: 'rgba(0, 0, 0, 0.35)',
            boxShadow: 'none',
            cursor: 'not-allowed'
          }
        }
      },
      {
        props: { variant: 'secondaryAction', disableElevation: false },
        style: {
          backgroundColor: '#fff',
          color: '#000',
          border: '1px solid #000',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            border: '1px solid #000'
          },
          '&.Mui-disabled': {
            backgroundColor: grey[300],
            color: 'rgba(0, 0, 0, 0.35)',
            borderColor: 'rgba(0, 0, 0, 0.15)',
            boxShadow: 'none',
            cursor: 'not-allowed'
          }
        }
      }
    ]
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '40px',
        '&:last-child': {
          paddingBottom: '40px'
        }
      }
    }
  },
  MuiCheckbox: {
    defaultProps: {
      size: 'small'
    },
    styleOverrides: {
      sizeSmall: {
        marginLeft: '8px',
        padding: 3,
        '& .MuiSvgIcon-root': {
          fontSize: 24
        }
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      labelSmall: {
        fontSize: '14px',
        paddingLeft: '8px',
        paddingRight: '8px'
      },
      labelMedium: {
        fontSize: '16px'
      }
    }
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        height: '100vh',
        ...displayCol,
        '> .page': {
          flex: 1,
          ...displayCol
        },
        '@media print': {
          '.web-only': {
            display: 'none'
          },
          '.print__no-break-inside': {
            pageBreakInside: 'avoid'
          },
          '.print__page-break-after': {
            pageBreakAfter: 'always'
          }
        }
      },
      html: {
        height: '100vh'
      }
    }
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderColor: 'rgba(0, 0, 0, 0.05)'
      }
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: '#000',
        fontWeight: 600,
        marginBottom: '8px'
      },
      asterisk: {
        color: red[500]
      }
    }
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: '13px',
        color: grey[700]
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        backgroundColor: '#fff'
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        marginTop: 0,
        backgroundColor: '#fff'
      }
    }
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        fontSize: '16px',
        '&.Mui-selected': {
          fontWeight: 600
        }
      }
    }
  },
  MuiRadio: {
    defaultProps: {
      size: 'small'
    },
    styleOverrides: {
      sizeSmall: {
        marginLeft: '8px',
        padding: 3,
        '& .MuiSvgIcon-root': {
          fontSize: 24
        }
      }
    }
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid',
        borderColor: secondary.main
      }
    }
  },
  MuiTab: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: lighten(secondary.light, 0.2)
        },
        fontWeight: 600,
        fontSize: 16
      }
    }
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        marginTop: 0
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: 14,
        padding: '12px 16px'
      }
    }
  },
  ...chartComponents
}
